import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Match } from '../../models/Match';
import TeamLogo from '../../components/TeamLogo';
import { divisionMappings, mapMappings } from '../../Constants';
import { parseMatchTime } from '../../utils/matchTime';
import styles from './MatchPage2.module.css';

interface Match2HeaderProps {
    matches: Match[];
    selectedMatch: Match | null;
    team1Score: number;
    team2Score: number;
    // Replaces the "division · season · date" line. Uploaded demos have no meaningful
    // division or season (they live in "UserUpload" / season 9999).
    meta?: string;
    // Uploaded demos use synthetic teams with no public page, so their names aren't links.
    linkTeams?: boolean;
}

// Match header: team1 | center score panel (orange accent bar, meta, score, format) | team2.
const Match2Header: React.FC<Match2HeaderProps> = ({
    matches, selectedMatch, team1Score, team2Score, meta: metaOverride, linkTeams = true,
}) => {
    const { t } = useTranslation('pages');
    const first = matches[0];
    const team1 = first.team1;
    const team2 = first.team2;

    const meta = metaOverride ?? [
        divisionMappings.get(first.division) ?? first.division,
        t('common:seasonNum', { number: first.season }),
        parseMatchTime(first.match_time).toLocaleDateString('da-DK').replaceAll('.', '/'),
    ].join(' · ');

    const mapLabel = (m: Match) => mapMappings.get(m.mapName.replace('*', '')) ?? m.mapName.replace('*', '');
    const format = selectedMatch
        ? mapLabel(selectedMatch)
        : t('match2.bestOf', { count: first.bo3 ? 3 : matches.length });

    // Same markup either way — a plain div when the team has no page to link to.
    const TeamSide: React.FC<{ teamId: string; className: string; children: React.ReactNode }> =
        ({ teamId, className, children }) => linkTeams
            ? <Link to={`/team/${teamId}`} className={className}>{children}</Link>
            : <div className={className}>{children}</div>;

    return (
        <div className={styles.header}>
            <TeamSide teamId={team1.teamId} className={styles.headerTeam}>
                <TeamLogo logo={team1.teamLogo} name={team1.teamName} teamId={team1.teamId} className={styles.headerLogo} />
                <span className={styles.headerTeamName}>{team1.teamName}</span>
            </TeamSide>
            <div className={styles.headerCenter}>
                <div className={styles.headerAccent} />
                <div className={styles.headerMeta}>{meta}</div>
                <div className={styles.headerScore}>
                    <span className={team1Score > team2Score ? styles.scoreWin : undefined}>{team1Score}</span>
                    <span className={styles.scoreColon}>:</span>
                    <span className={team2Score > team1Score ? styles.scoreWin : undefined}>{team2Score}</span>
                </div>
                <div className={styles.headerFormat}>{format}</div>
            </div>
            <TeamSide teamId={team2.teamId} className={`${styles.headerTeam} ${styles.headerTeamRight}`}>
                <span className={styles.headerTeamName}>{team2.teamName}</span>
                <TeamLogo logo={team2.teamLogo} name={team2.teamName} teamId={team2.teamId} className={styles.headerLogo} />
            </TeamSide>
        </div>
    );
};

export default Match2Header;
